import React, { useEffect, useMemo, useState } from "react";
import {
  FaSearch,
  FaPlus,
  FaEye,
  FaEdit,
  FaTimes,
  FaSyncAlt,
  FaExclamationCircle,
  FaCheckCircle,
  FaClock,
  FaFolderOpen,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { decryptData } from "../../utils/storageCrypto";
import TicketFormModal from "./Modals/TicketFormModal";
import TicketViewModal from "./Modals/TicketViewModal";

const API_BASE_URL = process.env.REACT_APP_FORE_BRICKBUCKET;
const TICKET_TYPES = ["CANCELLATION", "RETURN", "REFUND", "DELIVERY_ISSUE", "SUPPLIER_RETURN"];
const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
const STATUSES = ["OPEN", "ASSIGNED", "IN_PROGRESS", "RESOLVED", "CLOSED", "REJECTED"];

const statusColors = {
  OPEN: "bg-blue-100 text-blue-700",
  ASSIGNED: "bg-indigo-100 text-indigo-700",
  IN_PROGRESS: "bg-amber-100 text-amber-700",
  RESOLVED: "bg-green-100 text-green-700",
  CLOSED: "bg-gray-200 text-gray-700",
  REJECTED: "bg-red-100 text-red-700",
};

const priorityColors = {
  LOW: "bg-gray-100 text-gray-700",
  MEDIUM: "bg-blue-100 text-blue-700",
  HIGH: "bg-orange-100 text-orange-700",
  CRITICAL: "bg-red-100 text-red-700",
};

const formatLabel = (value = "") =>
  value.toString().replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());

const formatDate = (date) => {
  if (!date) return "—";
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const getTicketId = (item) => item?._id || item?.id;

export default function TicketsList() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [priorityFilter, setPriorityFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // modal state: { type: 'form' | 'view', mode?: 'create' | 'edit', item?: any }
  const [activeModal, setActiveModal] = useState(null);

  const getHeaders = () => {
    const encryptedToken = sessionStorage.getItem("token");
    const token = decryptData(encryptedToken);
    return {
      "Content-Type": "application/json",
      ...(token ? { "x-token": token } : {}),
    };
  };

  const apiRequest = async (path, options = {}) => {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: { ...getHeaders(), ...(options.headers || {}) },
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result.message || result.error || `Request failed status ${response.status}`);
    }
    return result;
  };

  const loadTickets = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await apiRequest("returns/get-tickets");
      const list = Array.isArray(result)
        ? result
        : result.tickets || result.data || result.data?.tickets || [];
      setTickets(list);
    } catch (err) {
      setError(err.message || "Failed to load tickets.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const showMessage = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3000);
  };

  const handleModalSuccess = (msg) => {
    showMessage(msg);
    loadTickets();
  };

  const handleTicketInlineUpdate = (id, updatedFields, msg) => {
    setTickets((prev) =>
      prev.map((t) => (getTicketId(t) === id ? { ...t, ...updatedFields } : t))
    );
    if (activeModal?.item && getTicketId(activeModal.item) === id) {
      setActiveModal((prev) => ({
        ...prev,
        item: { ...prev.item, ...updatedFields },
      }));
    }
    if (msg) showMessage(msg);
  };

  const filteredTickets = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter((item) => {
      const matchesSearch =
        !q ||
        [item.ticket_number, item.description, item.entity_type, item.entity_id, item.ticket_type, item.status]
          .some((v) => String(v || "").toLowerCase().includes(q));
      const matchesStatus = statusFilter === "ALL" || item.status === statusFilter;
      const matchesPriority = priorityFilter === "ALL" || item.priority === priorityFilter;
      const matchesType = typeFilter === "ALL" || item.ticket_type === typeFilter;
      return matchesSearch && matchesStatus && matchesPriority && matchesType;
    });
  }, [tickets, search, statusFilter, priorityFilter, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / pageSize));
  const visibleTickets = filteredTickets.slice((page - 1) * pageSize, page * pageSize);

  const summary = useMemo(
    () => ({
      total: tickets.length,
      open: tickets.filter((t) => t.status === "OPEN").length,
      inProgress: tickets.filter((t) => t.status === "IN_PROGRESS").length,
      resolved: tickets.filter((t) => t.status === "RESOLVED" || t.status === "CLOSED").length,
    }),
    [tickets]
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-7">
      {/* Top Header */}
      <div className="mb-7 flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="text-lg font-bold tracking-tight text-gray-900">Tickets Management</h1>
          <p className="mt-1 text-sm text-gray-500">Create, assign, track, and resolve tickets.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadTickets}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            <FaSyncAlt className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={() => setActiveModal({ type: "form", mode: "create" })}
            className="inline-flex items-center gap-2 rounded-lg bg-[#f7525a] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#f7525a]"
          >
            <FaPlus /> Create Ticket
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error && !activeModal && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <FaExclamationCircle className="mt-0.5 shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError("")} className="ml-auto"><FaTimes /></button>
        </div>
      )}
      {success && (
        <div className="mb-5 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          <FaCheckCircle /> {success}
        </div>
      )}

      {/* Summary Cards */}
      <div className="mb-7 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-gray-500">Total Tickets</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">{summary.total}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-gray-500">Open Tickets</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">{summary.open}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-gray-500">In Progress</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">{summary.inProgress}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-gray-500">Resolved / Closed</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">{summary.resolved}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <div className="relative xl:col-span-2">
            <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tickets..."
              className="w-full rounded-lg border border-gray-300 pl-9 py-2.5 text-sm"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-gray-300 py-2.5 text-sm">
            <option value="ALL">All Statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{formatLabel(s)}</option>)}
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="rounded-lg border border-gray-300 py-2.5 text-sm">
            <option value="ALL">All Priorities</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{formatLabel(p)}</option>)}
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="rounded-lg border border-gray-300 py-2.5 text-sm">
            <option value="ALL">All Types</option>
            {TICKET_TYPES.map((t) => <option key={t} value={t}>{formatLabel(t)}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-left text-sm">
            <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-5 py-4">Ticket</th>
                <th className="px-5 py-4">Type</th>
                <th className="px-5 py-4">Description</th>
                <th className="px-5 py-4">Priority</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Created</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="7" className="py-12 text-center text-gray-500">Loading tickets...</td></tr>
              ) : visibleTickets.length === 0 ? (
                <tr><td colSpan="7" className="py-12 text-center text-gray-500">No tickets found.</td></tr>
              ) : (
                visibleTickets.map((item) => (
                  <tr key={getTicketId(item)} className="hover:bg-gray-50">
                    <td className="px-5 py-4 font-semibold text-gray-900">{item.ticket_number}</td>
                    <td className="px-5 py-4">{formatLabel(item.ticket_type)}</td>
                    <td className="max-w-xs truncate px-5 py-4">
                      {item.description}
                      {item.entity_id && (
                        <span className="block text-xs text-gray-400">Order: {item.entity_id}</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${priorityColors[item.priority]}`}>
                        {formatLabel(item.priority)}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusColors[item.status]}`}>
                        {formatLabel(item.status)}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-500">{formatDate(item.createdAt)}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setActiveModal({ type: "view", item })}
                          className="rounded-lg border p-2 text-gray-600 hover:bg-blue-50 hover:text-blue-700"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => setActiveModal({ type: "form", mode: "edit", item })}
                          className="rounded-lg border p-2 text-gray-600 hover:bg-amber-50 hover:text-amber-700"
                        >
                          <FaEdit />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between border-t border-gray-200 px-5 py-4 text-sm text-gray-500">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border px-3 py-1.5 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border px-3 py-1.5 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Form Modal (Create / Edit) */}
      {activeModal?.type === "form" && (
        <TicketFormModal
          mode={activeModal.mode}
          initialData={activeModal.item}
          onClose={() => setActiveModal(null)}
          onSuccess={handleModalSuccess}
          apiRequest={apiRequest}
          backendBaseUrl={API_BASE_URL}
          getHeaders={getHeaders}
        />
      )}

      {/* View Modal */}
      {activeModal?.type === "view" && (
        <TicketViewModal
          ticket={activeModal.item}
          onClose={() => setActiveModal(null)}
          onEdit={() => setActiveModal({ type: "form", mode: "edit", item: activeModal.item })}
          apiRequest={apiRequest}
          onUpdateSuccess={handleTicketInlineUpdate}
        />
      )}
    </div>
  );
}